import { useGetBoolSettingQuery } from '../services/settingsApi';
import { useGetCurrentPlanQuery } from '../services/organizationApi';
import { isProfessionalPlan } from '../constants/moduleKeys';

export interface UseIsModuleEnabledOptions {
  /**
   * When true (default) the Profesional plan grants access even if the per-tenant
   * setting is false — mirrors the backend `[RequireModule(...)]` default. Pass
   * false to require explicit setting=true (manual-only / soft-launch shape).
   */
  allowPlanBypass?: boolean;
}

export interface UseIsModuleEnabledResult {
  /** True if the module should be visible. Defaults to false while loading. */
  enabled: boolean;
  /**
   * True while the underlying queries are loading. Use to render a Skeleton instead
   * of a 403, since `enabled` is false during the initial fetch.
   */
  isLoading: boolean;
}

/**
 * UX-layer mirror of the backend `IModuleAccessService` rule:
 *   enabled = setting === true OR (allowPlanBypass AND hasProBundle)
 * where hasProBundle = plan is Profesional OR trial still running (the 7-day trial
 * shows the full product; the server computes it so we never replicate trial dates).
 *
 * Backend is the real authority — this hook only avoids showing a menu entry or
 * route the API would reject.
 *
 * Two important properties:
 * 1. The plan comes from `/api/organization/current-plan` (local Organization.Plan
 *    column), NOT from Stripe. A slow or down Stripe must NOT hide Pro modules.
 * 2. setting=true short-circuits — `isLoading` drops to false as soon as the
 *    setting resolves to true, even if the plan query is still in flight. RTK
 *    Query caches the plan globally, so we only pay for the fetch once per
 *    session and let it run in parallel.
 */
export function useIsModuleEnabled(
  settingKey: string,
  options: UseIsModuleEnabledOptions = {},
): UseIsModuleEnabledResult {
  const { allowPlanBypass = true } = options;
  const { data: setting, isLoading: settingLoading } = useGetBoolSettingQuery(settingKey);
  const { data: currentPlan, isLoading: planLoading } = useGetCurrentPlanQuery(undefined, {
    skip: !allowPlanBypass,
  });

  const settingValue = setting?.value ?? false;
  // `hasProBundle` viene del servidor. El fallback por nombre de plan solo cubre una
  // respuesta cacheada de la API anterior, que no traía el campo.
  const hasProBundle = currentPlan?.hasProBundle ?? isProfessionalPlan(currentPlan?.plan);
  const planBypass = allowPlanBypass && hasProBundle;
  const enabled = settingValue || planBypass;

  // Short-circuit: once setting resolves to true, we are enabled regardless of the
  // plan query. Only block on plan when allowPlanBypass is on AND the setting is
  // not granting access on its own. Mirrors the backend ModuleAccessService order.
  const planRelevant = allowPlanBypass && !settingValue;
  const isLoading = settingLoading || (planRelevant && planLoading);

  return { enabled, isLoading };
}
